import { refreshAuthSingleFlight } from '@utils/networkSession';
import {
    abortNotificationStreamForRecovery,
    notifyNotificationStreamRecoveryComplete,
} from '@utils/notificationStreamBridge';

const RECOVERY_DEBOUNCE_MS = 8000;
export const MIN_HIDDEN_MS_FOR_WAKE_RECOVERY = 5000;
const CONNECTION_RELEASE_MS = 150;

let poolRecoveryPromise = null;
let lastRecoveryFinishedAt = 0;
let hiddenSince = null;

export function isTabBackgrounded() {
    return typeof document !== 'undefined' && document.hidden;
}

export function isPoolRecoveryInProgress() {
    return !!poolRecoveryPromise;
}

/** Returns the live recovery promise so callers can await it. Null when idle. */
export function getPoolRecoveryPromise() {
    return poolRecoveryPromise;
}

export function markTabHidden() {
    hiddenSince = Date.now();
}

export function markTabVisible() {
    const ms = hiddenSince != null ? Date.now() - hiddenSince : 0;
    hiddenSince = null;
    return { hiddenMs: ms };
}

export function shouldRunWakeRecovery(hiddenMs) {
    return hiddenMs >= MIN_HIDDEN_MS_FOR_WAKE_RECOVERY;
}

/**
 * Ordered pool recovery: pause queries → abort SSE + axios → release connections → refresh → batched refetch.
 * Safe to call from visibility wake, bfcache pageshow, or soft inflight detection.
 */
export async function runSessionPoolRecovery({ reason = 'wake', force = false } = {}) {
    if (poolRecoveryPromise) return poolRecoveryPromise;
    if (isTabBackgrounded()) return null;

    const now = Date.now();
    if (!force && reason === 'soft-inflight' && now - lastRecoveryFinishedAt < RECOVERY_DEBOUNCE_MS) {
        return null;
    }

    poolRecoveryPromise = (async () => {
        const {
            pauseAppQueriesForRecovery,
            resumeAppQueriesAfterRecovery,
            refetchActiveQueriesBatched,
        } = await import('@app/appQueryClient');

        pauseAppQueriesForRecovery();
        abortNotificationStreamForRecovery();
        const { abortAllSharedHttpInflight } = await import('@utils/http');
        abortAllSharedHttpInflight();

        await new Promise((resolve) => setTimeout(resolve, CONNECTION_RELEASE_MS));

        const tokens = await refreshAuthSingleFlight({ skipLogoutOnError: true });

        resumeAppQueriesAfterRecovery();

        notifyNotificationStreamRecoveryComplete();
        lastRecoveryFinishedAt = Date.now();

        // Clear the recovery promise flag *before* we trigger active query refetches,
        // so that the request interceptor does not cancel the incoming refetch requests.
        poolRecoveryPromise = null;

        if (tokens?.accessToken) {
            try {
                const m = await import('@services/realtime.socket');
                m.reconnectWithNewToken?.();
            } catch {
                // ignore
            }
        }

        const authStore = await import('@stores/auth.store');
        const hasToken = !!(tokens?.accessToken || authStore.useAuthStore.getState().accessToken);
        if (hasToken) {
            await refetchActiveQueriesBatched(3);
        }

        return tokens;
    })()
        .catch(() => null)
        .finally(() => {
            poolRecoveryPromise = null;
        });

    return poolRecoveryPromise;
}
